/**
 * Build a downloadable .ics file for a personalized watering schedule.
 *
 * The watering result + the user's restriction pattern already determine which
 * weekdays to water (via `buildScheduleDays` — the same logic that drives the
 * live preview chart), so the calendar export simply turns those on-days into a
 * single weekly-recurring VEVENT (FREQ=WEEKLY;BYDAY=...). Keeping the day
 * picking in `buildScheduleDays` means the .ics days always match what the user
 * sees on the page.
 *
 * Datetimes are emitted as floating local time so each user's calendar app
 * interprets them in their own zone (no TZID drift across regions). Mirrors the
 * convention in `lib/ics-builder.ts`.
 */
import { createEvents, type EventAttributes } from "ics"
import {
  buildScheduleDays,
  type RestrictionId,
} from "@/lib/watering-schedule-days"
import type { WateringScheduleResult } from "@/lib/watering-calculator"

const PRODUCT_ID = "-//What Grass Is This?//Watering Schedule//EN"

// ICS BYDAY codes indexed by JS day-of-week (0 = Sunday … 6 = Saturday).
const BYDAY_CODES = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"] as const

const WATERING_HOUR = 6 // 6:00 AM local — matches the preview's default time.

function slug(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^\w]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60)
}

/**
 * The soonest date whose weekday is one of `weekdays` (a set of 0–6 indices)
 * on which the watering time hasn't already passed. Returns [Y, M, D] with a
 * calendar (1-based) month. Today is skipped when `now` is already past
 * WATERING_HOUR so the first .ics instance never lands in the past; if today
 * is the only allowed weekday and it has passed, the next instance is the same
 * weekday next week (hence the 8-day search). Walking forward from a real Date
 * keeps month/year/leap-year rollover correct without hand-rolled arithmetic
 * (the recurring year-shift trap in date-window code — see lib/*-calculator.ts
 * history).
 */
export function nextWateringDate(
  weekdays: Set<number>,
  now: Date
): [number, number, number] {
  const todayPast = now.getHours() >= WATERING_HOUR
  for (let offset = 0; offset < 8; offset++) {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset)
    if (!weekdays.has(d.getDay())) continue
    if (offset === 0 && todayPast) continue
    return [d.getFullYear(), d.getMonth() + 1, d.getDate()]
  }
  // Unreachable when `weekdays` is non-empty; satisfies the type otherwise.
  return [now.getFullYear(), now.getMonth() + 1, now.getDate()]
}

export interface BuildWateringIcsResult {
  ics: string
  filename: string
}

/**
 * Returns the .ics payload + filename, or `null` when there's nothing to
 * schedule (a dormant season, a full rainfall skip, or zero sessions) — the
 * caller should hide the "add to calendar" affordance in that case.
 */
export function buildWateringScheduleIcs(
  result: WateringScheduleResult,
  restrictionId: RestrictionId,
  now: Date = new Date()
): BuildWateringIcsResult | null {
  if (result.sessionsPerWeek <= 0 || result.skipThisWeek) return null

  const gallonsPerSession =
    result.sessionsPerWeek > 0
      ? Math.round(result.weeklyGallons / result.sessionsPerWeek)
      : 0
  const days = buildScheduleDays({
    sessionsPerWeek: result.sessionsPerWeek,
    gallonsPerSession,
    runMinutes: result.sessionDuration,
    restrictionId,
  })

  const onDays = days.filter((d) => d.on).map((d) => d.day)
  if (onDays.length === 0) return null

  const byday = onDays.map((d) => BYDAY_CODES[d]).join(",")
  const [year, month, day] = nextWateringDate(new Set(onDays), now)

  // The whole watering pass (all zones run in sequence) is the calendar block;
  // fall back to the per-session duration for single-zone setups.
  const durationMinutes = Math.max(
    1,
    Math.round(result.controllerRunMinutesPerSession || result.sessionDuration)
  )

  const sizeLabel = result.lawnSize.toLocaleString()
  const description = [
    `Water your ${sizeLabel} sq ft ${result.grassType} lawn — about ${result.soilAdjustedWater.toFixed(
      1
    )} in/week across ${result.sessionsPerWeek} ${
      result.sessionsPerWeek === 1 ? "session" : "sessions"
    }.`,
    "",
    "Skip a session after meaningful rain, and adjust as the season changes.",
    "",
    "Full schedule: https://whatgrassisthis.com/tools/watering-schedule",
  ].join("\n")

  const event: EventAttributes = {
    uid: `watering-${slug(result.grassType)}-${restrictionId}@whatgrassisthis.com`,
    productId: PRODUCT_ID,
    start: [year, month, day, WATERING_HOUR, 0],
    startInputType: "local",
    startOutputType: "local",
    duration: { minutes: durationMinutes },
    title: `💧 Water the lawn (${result.grassType})`,
    description,
    categories: ["Lawn Care", "Watering"],
    recurrenceRule: `FREQ=WEEKLY;INTERVAL=1;BYDAY=${byday}`,
    url: "https://whatgrassisthis.com/tools/watering-schedule",
    status: "CONFIRMED",
    busyStatus: "FREE",
  }

  const { error, value } = createEvents([event])
  if (error || !value) {
    throw error || new Error("Failed to build watering ICS")
  }

  return {
    ics: value,
    filename: `watering-schedule-${slug(result.grassType)}.ics`,
  }
}
